import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import lodash from 'lodash'
import { justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import {
  ItemDataWithBatch, TaxCouponCreateBatchPayload, TaxCouponCreateReceivementPayload, TaxCouponState, TaxCouponValidationBarcodePayload,
  EntryData, EntryDataWithFC, updateIdMeasureUnitType, SetDirectGondola, GetDirectGondola, ReceivementUnitChange, ReceivementUnitChangePayload,
} from './TaxCouponTypes'

const initialState: TaxCouponState = {
  taxCoupon: '',
  couponDate: new Date(),
  userBadge: '',
  dock: '',
  items: [],
  barcodeAttempted: '',
  scannedIdModelo: 0,
  scannedDescription: '',
  shouldOpenSearchModal: false,
  idMeasure: 0,
  batchesDirectGondola: [],
  receivementUnitChange: true,
  directGondolaPrintLimit: 5,
}

const validateEntryData = justForSideEffectWithPayload<EntryDataWithFC>()
const scanBarcode = justForSideEffectWithPayload<TaxCouponValidationBarcodePayload>()
const searchProduct = justForSideEffectWithPayload<string>()
const sendTaxCouponEntry = justForSideEffectWithPayload<any>()
const createTaxCouponReceivement = justForSideEffectWithPayload<TaxCouponCreateReceivementPayload>()
const createTaxCouponBatch = justForSideEffectWithPayload<TaxCouponCreateBatchPayload>()
const getReceivementUnitChange = justForSideEffectWithPayload<ReceivementUnitChangePayload>()
const getDirectGondola = justForSideEffectWithPayload<GetDirectGondola>()
const deleteTaxCouponBatch = justForSideEffectWithPayload<number>()

const setEntryData = (state: TaxCouponState, action: PayloadAction<EntryData>) => ({
  ...state,
  taxCoupon: action.payload.taxCoupon,
  couponDate: action.payload.couponDate,
  userBadge: action.payload.userBadge,
  dock: action.payload.dock,
})

const addItem = (state: TaxCouponState, action: PayloadAction<ItemDataWithBatch>) => ({
  ...state,
  items: [...state.items, action.payload],
})

const setBarcodeAttempted = (state: TaxCouponState, action: PayloadAction<string>) => ({
  ...state,
  barcodeAttempted: action.payload,
})

const setBarcodeData = (state: TaxCouponState, action: PayloadAction<{
  idModelo: number, description: string}>) => ({
  ...state,
  scannedIdModelo: action.payload.idModelo,
  scannedDescription: action.payload.description,
})

const shouldOpenSearchModal = (state: TaxCouponState, action: PayloadAction<boolean>) => ({
  ...state,
  shouldOpenSearchModal: action.payload,
})

const clearTaxCouponData = () => ({
  ...initialState,
})

const setReceivement = (state: TaxCouponState, action: PayloadAction<number>) => ({
  ...state,
  idReceivement: action.payload,
})

const setBatch = (state: TaxCouponState, action: PayloadAction<number>) => ({
  ...state,
  idBatch: action.payload,
})

const deleteBatchFromItems = (state: TaxCouponState, action: PayloadAction<number>) => {
  const batches = lodash.cloneDeep(state.items)

  const newBatches = batches.filter((batch) => batch.idBatch !== action.payload)

  return {
    ...state,
    items: newBatches,
  }
}

const updateIdMeasureUnit = (state, action: PayloadAction<updateIdMeasureUnitType>) => {
  const { key, value } = action.payload
  return { ...state, [key]: value }
}

const setDirectGondola = (state: TaxCouponState, action: PayloadAction<SetDirectGondola>) => ({
  ...state,
  batchesDirectGondola: action.payload.batchesDirectGondola,
  directGondolaPrintLimit: action.payload.directGondolaPrintLimit,
})

const setReceivementUnitChange = (state: TaxCouponState, action: PayloadAction<ReceivementUnitChange>) => ({
  ...state,
  receivementUnitChange: action.payload.receivementUnitChange,
})

const slice = createSlice({
  name: 'taxCoupon',
  initialState,
  reducers: {
    validateEntryData,
    setEntryData,
    scanBarcode,
    searchProduct,
    sendTaxCouponEntry,
    addItem,
    setBarcodeAttempted,
    setBarcodeData,
    shouldOpenSearchModal,
    clearTaxCouponData,
    createTaxCouponReceivement,
    createTaxCouponBatch,
    setReceivement,
    setBatch,
    updateIdMeasureUnit,
    getDirectGondola,
    setDirectGondola,
    getReceivementUnitChange,
    setReceivementUnitChange,
    deleteTaxCouponBatch,
    deleteBatchFromItems,
  },
})

export default slice.reducer

export const { actions } = slice
